import { useMutation } from '@tanstack/react-query';
import { Chat, GptModel, MAX_CHAT_TITLE_LENGTH } from '../../types';
import useApi from '../../../api/backendApiContext';
import useAuth from '../../../auth/authContext';
import { useQueryCacheManager } from '../../../hooks/useQueryCacheManager';
import { analyticsTrack } from '../../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../../web-analytics/AnalyticsEvent';
import { ChatType } from '../../dto';
import { ComplexContent } from '../components/TextMessageInput';

const ELLIPSIS = '…';
const getTitle = (content: string) => {
  const title =
    content.length > MAX_CHAT_TITLE_LENGTH
      ? content
          .trim()
          .substring(0, MAX_CHAT_TITLE_LENGTH - ELLIPSIS.length)
          .trim() + ELLIPSIS
      : content.trim();
  return title ? title : 'New chat';
};

export const useCreateChatMutation = (
  onSuccess: (chat: Chat) => void,
  onError: (e: unknown, variables: { firstMessageContent: ComplexContent; model: GptModel }) => void
) => {
  const { growegyAIApi } = useApi();
  const {
    state: { user },
  } = useAuth();
  const queryCacheManager = useQueryCacheManager();
  return useMutation(
    async (props: {
      firstMessageContent: ComplexContent;
      model: GptModel;
      type: ChatType;
      subtype: string | null;
    }) => {
      const { firstMessageContent, model, type, subtype } = props;
      const title = getTitle(firstMessageContent.plainTextContent);
      return growegyAIApi.createChat(
        user!.userId,
        model.id,
        title.replaceAll('\n', ' '),
        type,
        subtype
      );
    },
    {
      onSuccess: (chat) => {
        if (!chat) return;
        queryCacheManager.onChatCreated(chat);
        analyticsTrack(AnalyticsEvent.CHAT_CREATED, {
          type: chat.type,
          model: chat.model.id,
        });
        onSuccess(chat);
      },
      onError,
    }
  );
};
